import { useCallback } from "react"
import { useSelector } from "react-redux"

import Media from "components/ui/media"
import Shimmer from "components/ui/shimmer"
import { IS_JEWLR, isTestMode } from "helpers/application"
import { isSolidGold } from "helpers/product/sku-colors"
import scrollToElement from "helpers/scroll-to-element"
import { cn } from "styles/utils"

import ProductPrice from "../product-price"

import Financing from "./financing"
import Promo from "./promo"
import Ratings from "./ratings"

const WizardHeader = () => {
  const price = useSelector((state) =>
    Object.keys(state.wizardUI.currentPricing || {}).length > 0
      ? state.wizardUI.currentPricing.price_to_display * 100
      : 0
  )
  const title = useSelector((state) =>
    state.wizard.loading ? "" : state.wizard.title
  )
  const wizardLoading = useSelector((state) => state.wizard.loading)
  const hideMiniview = useSelector(
    (state) => state.previewImageUI.miniview.scrollHide
  )
  const selectedSku = useSelector(
    (state) => state.choices && state.choices.sku && state.choices.sku.value
  )
  const tags = useSelector((state) => state.wizard.tags)
  const promo = useSelector((state) => state.sale.promo)
  const { code, product_tag, promo_badge, badge_className } = promo || {}

  const enablePromoBadge =
    (promo_badge || code === "BESTSALE") &&
    (!product_tag || (tags || []).includes(product_tag))

  const promoBadge =
    code === "BESTSALE"
      ? isSolidGold(selectedSku)
        ? `${sl.config.sale_key.includes("cyber-monday") ? "CYBER MONDAY" : "BLACK FRIDAY"}! Extra 15% OFF Solid Gold* | Code BESTSALE`
        : `${sl.config.sale_key.includes("cyber-monday") ? "CYBER MONDAY" : "BLACK FRIDAY"}! Extra 20% OFF* | Code: BESTSALE`
      : promo_badge

  const holdbackExp = useSelector(
    (state) => state.experiment.holdback.variant_id
  )
  const handleRatingsClick = useCallback(() => {
    scrollToElement("#product-reviews", hideMiniview ? 90 : 350)
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-reviews-count-pdp",
      })
    }
  }, [hideMiniview])

  const financing = <Financing price={price} />
  const ratings = <Ratings handleClick={handleRatingsClick} />

  return (
    <section className="tablet:mb-12 mb-16">
      <Media greaterThanOrEqual="tablet">
        <Promo />
      </Media>
      {wizardLoading ? (
        <Shimmer
          className="tablet:h-(--tablet-height) tablet-w-(--tablet-width) h-(--base-height) mb-8 w-full rounded-sm"
          style={{
            "--base-height": "42px",
            "--tablet-height": "28px",
            "--tablet-width": "320px",
          }}
        />
      ) : (
        <>
          <h1 className="text-18 tablet:text-20 leading-22 tablet:leading-28 font-bold">
            {title}
          </h1>
          {IS_JEWLR() && holdbackExp === 1 && (
            <Media lessThan="tablet">{ratings}</Media>
          )}
        </>
      )}
      <ProductPrice />
      {IS_JEWLR() && (
        <>
          {wizardLoading ? (
            <Shimmer
              className={cn(
                "h-(--base-height) tablet:mt-12 mt-16 w-full rounded-sm",
                sl.config.klarna_osm_pdp && "tablet:h-(--tablet-height)"
              )}
              style={{
                "--base-height": sl.config.klarna_osm_pdp ? "38px" : "18px",
                "--tablet-height": "21px",
              }}
            />
          ) : (
            <>
              {enablePromoBadge && (
                <div
                  className={cn(
                    "bg-jade leading-12 rounded-xs text-black-print text-12 mt-8 inline-flex items-center justify-center p-8 font-semibold",
                    badge_className
                  )}
                >
                  {promoBadge}
                </div>
              )}
              <div
                className={cn(
                  "min-h-(--base-min-height) tablet:mt-12 mt-16",
                  sl.config.klarna_osm_pdp &&
                    "tablet:min-h-(--tablet-min-height)"
                )}
                style={{
                  "--base-min-height": sl.config.klarna_osm_pdp
                    ? "38px"
                    : "18px",
                  "--tablet-min-height": "21px",
                }}
              >
                <Media lessThan="tablet"> {financing}</Media>
                <Media
                  className={cn(
                    "flex justify-between gap-8",
                    sl.config.klarna_osm_pdp ? "items-center" : "items-end"
                  )}
                  greaterThanOrEqual="tablet"
                >
                  {ratings}
                  {financing}
                </Media>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

export default WizardHeader
